import { useMemo } from 'react';

interface Option {
  value: string;
  label: string;
  // optional text matched against the search query instead of `label`; lets an
  // option be found by multiple terms (e.g. both its alias and its URL)
  searchText?: string;
}

interface UseLimitedComboboxItemsParams<T extends Option> {
  items: T[];
  searchValue: string;
  /** Single selected value (single-select comboboxes). */
  selectedValue?: string;
  /** Selected values (multi-select comboboxes). Takes precedence over `selectedValue`. */
  selectedValues?: string[];
}

// Rendering very large option lists hurts combobox usability and can cause
// browser-side rendering/interaction lag, so we cap visible search results to
// a manageable amount.
const MAX_RENDERED_SEARCH_RESULTS = 150;

export function useLimitedComboboxItems<T extends Option>({
  items,
  searchValue,
  selectedValue,
  selectedValues,
}: UseLimitedComboboxItemsParams<T>) {
  const requiresTypeAhead = items.length > MAX_RENDERED_SEARCH_RESULTS;

  // Normalize single/multi selection into a stable lookup key so the memo only
  // recomputes when the actual selection changes, not on every render.
  const selectedKey = selectedValues?.length
    ? selectedValues.join(' ')
    : (selectedValue ?? '');

  const comboboxItems = useMemo(() => {
    if (!items.length) return [];

    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!requiresTypeAhead) {
      if (!normalizedSearch) return items;
      return items.filter((item) =>
        getSearchableText(item).includes(normalizedSearch),
      );
    }

    if (!normalizedSearch) {
      // Keep selected options visible without replacing the unselected choices.
      // Replacing the list with only selected options makes a multi-select look
      // as if no additional domains can be picked after the first selection.
      const selectedSet = new Set(selectedKey ? selectedKey.split(' ') : []);
      const selectedItems = items.filter((item) => selectedSet.has(item.value));
      const remainingItemSlots = Math.max(
        0,
        MAX_RENDERED_SEARCH_RESULTS - selectedItems.length,
      );

      return [
        ...selectedItems,
        ...items
          .filter((item) => !selectedSet.has(item.value))
          .slice(0, remainingItemSlots),
      ];
    }

    return collectMatchingItems(
      items,
      normalizedSearch,
      MAX_RENDERED_SEARCH_RESULTS,
    );
  }, [items, requiresTypeAhead, searchValue, selectedKey]);

  return { comboboxItems, requiresTypeAhead };
}

function collectMatchingItems<T extends Option>(
  items: T[],
  normalizedSearch: string,
  limit: number,
) {
  const matches: T[] = [];

  for (const item of items) {
    if (!getSearchableText(item).includes(normalizedSearch)) {
      continue;
    }

    matches.push(item);

    if (matches.length >= limit) {
      break;
    }
  }

  return matches;
}

function getSearchableText(item: Option): string {
  return (item.searchText ?? item.label).toLowerCase();
}
